import { useState } from "react";
import { Users, ShieldBan } from "lucide-react";
import { fmtDate } from "@/lib/api";
import { CONTACT_EMAIL } from "@/lib/legal";
import { Logo } from "@/components/Logo";
import { useChat } from "@/hooks/useChat";
import { VideoTile } from "@/components/chat/VideoTile";
import { MessageList } from "@/components/chat/Messages";
import { Composer } from "@/components/chat/Composer";
import { Controls } from "@/components/chat/Controls";

const STATUS = {
  connecting: { t: "Kapcsolódás…", c: "#FFD600" },
  waiting: { t: "Partner keresése…", c: "#FFD600" },
  chatting: { t: "Kapcsolódva", c: "#00E676" },
  left: { t: "A partner kilépett", c: "#FF3B6C" },
  stopped: { t: "Beszélgetés vége", c: "#FF3B6C" },
  terminated: { t: "A moderátor lezárta", c: "#FF3B6C" },
  disconnected: { t: "Kapcsolat megszakadt – frissítsd az oldalt", c: "#EF4444" },
  banned: { t: "Ki vagy tiltva", c: "#EF4444" },
};

const BannedNote = ({ ban }) => (
  <div className="flex flex-col items-center px-4 py-10 text-center" data-testid="banned-notice">
    <ShieldBan className="mb-4 h-14 w-14 text-[#EF4444]" />
    <p className="font-display text-2xl font-bold">Ki lettél tiltva</p>
    <p className="mt-2 text-sm text-muted-foreground">
      {ban?.expires_at ? `A tiltás lejár: ${fmtDate(ban.expires_at)}` : "A tiltás végleges."}
      {ban?.reason ? ` Indok: ${ban.reason}` : ""}
    </p>
    <p className="mt-3 text-xs text-muted-foreground">Ha szerinted tévedés történt, írj nekünk: {CONTACT_EMAIL}</p>
  </div>
);

const SystemNote = ({ status, partnerNick, ban }) => {
  if (status === "banned") return <BannedNote ban={ban} />;
  if (status === "waiting" || status === "connecting")
    return (
      <div className="flex flex-col items-center py-10 text-center" data-testid="searching-indicator">
        <div className="dot-bounce mb-4"><span className="bg-[#FF3B6C]" /><span className="bg-[#00F0FF]" /><span className="bg-[#FFD600]" /></div>
        <p className="font-display text-lg font-bold">Keresünk neked valakit…</p>
        <p className="text-sm text-muted-foreground">Addig kapcsold be a kamerát vagy a mikrofont, ha szeretnéd.</p>
      </div>
    );
  if (status === "chatting")
    return <p className="text-center text-xs text-muted-foreground" data-testid="matched-note">Most már <b className="text-white">{partnerNick || "Ismeretlen"}</b> partnerrel beszélgetsz. Köszönj!</p>;
  return <p className="py-6 text-center text-sm text-muted-foreground" data-testid="ended-note">{STATUS[status]?.t}. Indíts új beszélgetést!</p>;
};

export default function Chat() {
  const [nick] = useState(() => sessionStorage.getItem("esc_nick") || "Ismeretlen");
  const chat = useChat(nick);
  const st = STATUS[chat.status];

  return (
    <div className="grain flex h-[100dvh] flex-col" data-testid="chat-room-container">
      <header className="relative z-10 flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 md:px-6">
        <Logo size="text-xl" />
        <div className="flex items-center gap-2 rounded-full bg-white/5 px-4 py-1.5 text-sm font-semibold" data-testid="chat-status">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: st.c, boxShadow: `0 0 12px ${st.c}` }} />
          <span className="max-w-[40vw] truncate">{st.t}</span>
        </div>
        <span className="hidden items-center gap-1.5 text-sm text-muted-foreground sm:flex" data-testid="chat-online-count"><Users className="h-4 w-4" /> {chat.online}</span>
      </header>

      <main className="relative z-10 grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)_auto] gap-4 p-3 md:p-5 lg:grid-cols-[minmax(0,1fr)_420px] lg:grid-rows-[minmax(0,1fr)_auto]">
        <section className="flex min-h-0 flex-col lg:col-start-1 lg:row-start-1">
          <div className="relative aspect-video w-full lg:aspect-auto lg:min-h-0 lg:flex-1">
            <VideoTile testId="remote-video-tile" stream={chat.remoteStream} camOn={chat.status === "chatting" && chat.partnerMedia.cam}
              micOn={chat.partnerMedia.mic} label={chat.status === "chatting" ? chat.partnerNick || "Ismeretlen" : "—"} accent="#7C3AED" />
            <div className="absolute right-3 top-3 aspect-[4/3] w-28 sm:w-44">
              <VideoTile testId="local-video-tile" small muted stream={chat.localStream} version={chat.localVersion}
                camOn={chat.media.cam} micOn={chat.media.mic} label="Te" accent="#FF3B6C" />
            </div>
          </div>
        </section>

        <section className="flex min-h-0 flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#1A152E]/80 backdrop-blur-xl lg:col-start-2 lg:row-span-2 lg:row-start-1">
          <MessageList messages={chat.messages} partnerNick={chat.partnerNick} typing={chat.partnerTyping}>
            <SystemNote status={chat.status} partnerNick={chat.partnerNick} ban={chat.ban} />
          </MessageList>
          <Composer chat={chat} />
        </section>
        <div className="lg:col-start-1 lg:row-start-2"><Controls chat={chat} /></div>
      </main>
    </div>
  );
}
